import { useMemo } from "react";

import { API_URLS } from "shared/constants";
import useHttpActions from "shared/services/api/core/useHttpActions";

import BaseConverter from "./converters/baseConverter";

import type { BackendEntityType, InvitationStatus, PaginationParamsModel, PaginationTokenType } from "../types";
import { paginationTokenResolver } from "../types";
import type { WaiverModel, WaiverSignatureModel } from "../types/Waiver";
import { waiverResolver, waiverSignatureResolver } from "../types/Waiver";

const useWaiversService = () => {
	const actions = useHttpActions();

	return useMemo(
		() => ({
			createSoccerWaiver: async (waivers: Partial<WaiverModel>[]) => {
				return BaseConverter.convert<{ waivers: { id: string }[] }>(
					await actions.post(API_URLS.SOCCER.CREATE_WAIVER, { waivers })
				);
			},
			removeSoccerWaiver: async (waiverId: string) => {
				return BaseConverter.convert<{ success: boolean }>(
					await actions.post(API_URLS.SOCCER.REMOVE_WAIVER, { waiverId })
				);
			},
			acceptWaiver: async (waiverSignatureId: string, asPersonId?: string) => {
				return BaseConverter.convert<{ success: boolean }>(
					await actions.post(API_URLS.WAIVERS.ACCEPT, {
						waiverSignatureId,
						asPersonId
					})
				);
			},
			listUserWaivers: async (params: { status?: InvitationStatus | InvitationStatus[] } & PaginationParamsModel) => {
				return BaseConverter.convert<{
					waiverSignatures: WaiverSignatureModel[];
					nextToken: PaginationTokenType;
				}>(
					await actions.get(API_URLS.WAIVERS.LIST_USER_WAIVERS, params, {
						resolvers: [
							{
								fields: ["waiverSignatures"],
								resolver: waiverSignatureResolver
							},
							{
								resolver: paginationTokenResolver,
								fields: ["nextToken"]
							}
						]
					})
				);
			},
			listEntityWaivers: async (
				params: {
					status?: InvitationStatus | InvitationStatus[];
					objectId: string;
					objectType: BackendEntityType;
				} & PaginationParamsModel
			) => {
				return BaseConverter.convert<{
					waivers: WaiverModel[];
					nextToken: PaginationTokenType;
				}>(
					await actions.get(API_URLS.SOCCER.LIST_WAIVERS, params, {
						resolvers: [
							{
								fields: ["waivers"],
								resolver: waiverResolver
							},
							{
								resolver: paginationTokenResolver,
								fields: ["nextToken"]
							}
						]
					})
				);
			}
		}),
		[actions]
	);
};

export default useWaiversService;
